// src/components/BingoBall.jsx
import React, { useCallback, useMemo } from "react";

// Define new sizes for BingoBall
const BALL_SIZES = {
  lg: 90,
  md: 60, // New medium size
  sm: 50,
  xs: 45, // New extra small size
  xxs: 40, // New extra extra small size
};

/**
 * Helper function to determine the background color of a Bingo ball based on its number.
 * "F" (Free space) has a distinct color.
 * @param {number|string} num - The Bingo number or "F".
 * @returns {string} - Hex color code.
 */
export const getBallColor = (num) => {
  if (num === "F") return "#FFEE33"; // Free space color
  if (num <= 15) return "#FF3333"; // B
  if (num <= 30) return "#3388FF"; // I
  if (num <= 45) return "#AA33FF"; // N
  if (num <= 60) return "#33FF66"; // G
  return "#FFEE33"; // O
};

/**
 * Helper function to determine the letter prefix for a Bingo number.
 * @param {number|string} num - The Bingo number or "F".
 * @returns {string} - The Bingo letter (B, I, N, G, O) or empty string for "F".
 */
export const getBallLetter = (num) => {
  if (!num) return "";
  if (num === "F") return "";
  if (num <= 15) return "B";
  if (num <= 30) return "I";
  if (num <= 45) return "N";
  if (num <= 60) return "G";
  else return "O";
};

/**
 * BingoBall component displays a single Bingo number with its corresponding letter and color.
 * It includes an optional flip animation.
 * @param {object} props - Component props.
 * @param {number|string} props.number - The Bingo number or "F" for free space.
 * @param {string} [props.size="md"] - The size of the Bingo ball (lg, md, sm, xs, xxs).
 * @param {boolean} [props.flip=false] - Whether to apply a flip animation.
 */
const BingoBall = React.memo(({ number, size = "md", flip = false }) => {
  const letter = getBallLetter(number);
  const ballColor = getBallColor(number);

  // Memoize dimension calculations to prevent re-calculation on every render
  const {
    baseSize,
    outerRingWidth,
    whiteRingWidth,
    innerBallRingWidth,
    letterFontSize,
    numberFontSize,
  } = useMemo(() => {
    const base = BALL_SIZES[size] || BALL_SIZES.md;
    return {
      baseSize: base,
      outerRingWidth: base * 0.17,
      whiteRingWidth: base * 0.06,
      innerBallRingWidth: base * 0.05,
      letterFontSize: base * 0.18,
      numberFontSize: base * 0.18,
    };
  }, [size]);

  return (
    <div
      style={{
        position: "relative",
        width: baseSize,
        height: baseSize,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        userSelect: "none",
        flexShrink: 0, // Prevent shrinking in flex containers
      }}
      className={flip ? "animate-flip-in" : ""} // Apply flip animation
    >
      {/* Outer colored ring */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: baseSize,
          height: baseSize,
          borderRadius: "50%",
          backgroundColor: ballColor,
          border: `2px solid ${ballColor}`,
          boxSizing: "border-box",
          boxShadow: `0 0 ${baseSize * 0.05}px rgba(0,0,0,0.3)`, // Soft outer shadow
        }}
      />

      {/* White ring inside */}
      <div
        style={{
          position: "absolute",
          top: outerRingWidth,
          left: outerRingWidth,
          width: baseSize - 2 * outerRingWidth,
          height: baseSize - 2 * outerRingWidth,
          borderRadius: "50%",
          backgroundColor: "#ffffff",
          border: "1.5px solid #eee",
          boxSizing: "border-box",
          boxShadow: "inset 0 0 5px rgba(0,0,0,0.2)", // Inner shadow for depth
        }}
      />

      {/* Inner colored ring */}
      <div
        style={{
          position: "absolute",
          top: outerRingWidth + whiteRingWidth,
          left: outerRingWidth + whiteRingWidth,
          width: baseSize - 2 * (outerRingWidth + whiteRingWidth),
          height: baseSize - 2 * (outerRingWidth + whiteRingWidth),
          borderRadius: "50%",
          backgroundColor: ballColor,
          border: `1.5px solid ${ballColor}`,
          boxSizing: "border-sizing",
          boxShadow: "inset 0 0 8px rgba(0,0,0,0.3)", // Deeper inner shadow
        }}
      />

      {/* Center white circle with number and letter */}
      <div
        style={{
          position: "absolute",
          top: outerRingWidth + whiteRingWidth + innerBallRingWidth,
          left: outerRingWidth + whiteRingWidth + innerBallRingWidth,
          width:
            baseSize -
            2 * (outerRingWidth + whiteRingWidth + innerBallRingWidth),
          height:
            baseSize -
            2 * (outerRingWidth + whiteRingWidth + innerBallRingWidth),
          borderRadius: "50%",
          backgroundColor: "#ffffff",
          boxShadow: "inset 0 0 4px rgba(0,0,0,0.4)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily:"Georgia",
          cursor: "default",
          // Add a subtle gradient for a more spherical look
          background: `radial-gradient(circle at 30% 30%, #ffffff, ${ballColor} 100%)`,
        }}
      >
        {/* Top-left highlight for glossy effect */}
        <div
          style={{
            position: "absolute",
            top: "-10%",
            left: "-10%",
            width: "60%",
            height: "30%",
            backgroundColor: "white",
            opacity: 0.15,
            filter: "blur(3px)",
            borderRadius: "50%",
            transform: "rotate(45deg)",
            pointerEvents: "none",
          }}
        />

        {/* Bingo letter */}
        <span
          style={{
            color: "black",
            fontWeight: "bold",
            fontSize: letterFontSize,
            lineHeight: 1,
            zIndex: 1,
            textShadow: "0 1px 2px rgba(255,255,255,0.5)", // Light text shadow
          }}
        >
          {letter}
        </span>
        {/* Bingo number */}
        <span
          style={{
            color: "black",
            fontWeight: "800",
            fontSize: numberFontSize,
            lineHeight: 1,
            zIndex: 1,
            textShadow: "0 1px 2px rgba(255,255,255,0.5)", // Light text shadow
          }}
        >
          {number}
        </span>
      </div>
    </div>
  );
});

export default BingoBall;
